import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Stack, Title, Text, Alert, Paper } from '@mantine/core';
import { IconCheck, IconAlertCircle } from '@tabler/icons-react';
import { QRScanner } from './components/QRScanner';
import { notifications } from '@mantine/notifications';
import { useAssets } from '@/hooks/api/useAssets';
import { useOrganization } from '@/hooks/useOrganization';

export function ScanQRPage() {
  const navigate = useNavigate();
  const { organizationId } = useOrganization();
  const { data: assets = [] } = useAssets(organizationId || '');
  const [scannedData, setScannedData] = useState<string>('');
  const [success, setSuccess] = useState(false);

  const handleScan = (data: string) => {
    setScannedData(data);

    // Check if it's a URL (new format)
    if (data.startsWith('http://') || data.startsWith('https://')) {
      try {
        const url = new URL(data);

        // Check if it's our asset token URL format: /a/:token
        if (url.pathname.startsWith('/a/')) {
          setSuccess(true);
          notifications.show({
            title: 'Asset QR Code Scanned',
            message: 'Redirecting to asset details...',
            color: 'green',
            icon: <IconCheck size={16} />,
          });

          // Navigate to the token URL (will handle auth and redirect)
          setTimeout(() => {
            navigate(url.pathname);
          }, 1500);
          return;
        }

        // Check if it's the old URL param format: /assets?view=:id
        if (url.pathname === '/assets' && url.searchParams.has('view')) {
          const assetId = url.searchParams.get('view');
          setSuccess(true);
          notifications.show({
            title: 'Asset QR Code Scanned',
            message: 'Redirecting to asset details...',
            color: 'green',
            icon: <IconCheck size={16} />,
          });

          setTimeout(() => {
            navigate(`/assets?view=${assetId}`);
          }, 1500);
          return;
        }

        // Unknown URL format
        notifications.show({
          title: 'Invalid QR Code',
          message: 'This URL is not recognized as an asset QR code',
          color: 'red',
          icon: <IconAlertCircle size={16} />,
        });
      } catch (err) {
        notifications.show({
          title: 'Invalid URL',
          message: 'The scanned QR code contains an invalid URL',
          color: 'red',
          icon: <IconAlertCircle size={16} />,
        });
      }
      return;
    }

    // Try parsing as JSON (old format - for backward compatibility)
    try {
      const parsed = JSON.parse(data);

      if (parsed.type === 'asset') {
        // Find the asset
        const asset = assets.find((a) => a.id === parsed.id);

        if (asset) {
          setSuccess(true);
          notifications.show({
            title: 'Asset Found',
            message: `Asset ${parsed.tag_id} has been scanned successfully`,
            color: 'green',
            icon: <IconCheck size={16} />,
          });

          // Navigate to assets page with asset ID in URL params to open details modal
          setTimeout(() => {
            navigate(`/assets?view=${parsed.id}`);
          }, 1500);
        } else {
          notifications.show({
            title: 'Asset Not Found',
            message: `Asset ${parsed.tag_id} could not be found in your organization`,
            color: 'orange',
            icon: <IconAlertCircle size={16} />,
          });
        }
      } else {
        notifications.show({
          title: 'Invalid QR Code',
          message: 'This QR code is not a valid asset QR code',
          color: 'red',
          icon: <IconAlertCircle size={16} />,
        });
      }
    } catch (error) {
      notifications.show({
        title: 'Invalid QR Code',
        message: 'Could not parse QR code data. Please scan a valid asset QR code.',
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
    }
  };

  return (
    <Container size="sm" py="xl">
      <Stack gap="lg">
        <div>
          <Title order={2} mb="xs">
            Scan Asset QR Code
          </Title>
          <Text size="sm" c="dimmed">
            Use your device camera to scan an asset QR code for quick access to asset details
          </Text>
        </div>

        {success && (
          <Alert icon={<IconCheck size={16} />} title="Success" color="green">
            QR code scanned successfully! Redirecting...
          </Alert>
        )}

        <Paper p="md" withBorder>
          <QRScanner onScan={handleScan} onClose={() => navigate('/assets')} />
        </Paper>

        {scannedData && !success && (
          <Paper p="md" withBorder bg="gray.0">
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Scanned Data:
              </Text>
              <Text size="xs" c="dimmed" style={{ wordBreak: 'break-all' }}>
                {scannedData}
              </Text>
            </Stack>
          </Paper>
        )}
      </Stack>
    </Container>
  );
}
